import { useParams, Link } from 'react-router-dom';
import { articles, categories, platforms } from '@/mocks/data';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === Number(id));

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-50">
        <div className="text-center">
          <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center bg-background-100 rounded-full">
            <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
          </div>
          <h1 className="text-xl font-heading font-bold text-foreground-900 mb-2">文章未找到</h1>
          <p className="text-sm text-foreground-500 mb-6">该文章不存在或已被移除</p>
          <Link to="/" className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-white rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
            <i className="ri-home-line"></i> 返回首页
          </Link>
        </div>
      </div>
    );
  }

  const category = categories.find((c) => c.slug === article.category);
  const relatedArticles = articles
    .filter((a) => a.id !== article.id && (a.category === article.category || article.relatedPlatforms.some((p) => a.platform === p)))
    .slice(0, 3);

  const parseContent = (content: string) => {
    const lines = content.split('\n');
    const elements: React.ReactNode[] = [];
    let i = 0;

    while (i < lines.length) {
      const line = lines[i];

      if (line.startsWith('## ')) {
        elements.push(
          <h2 key={i} className="text-xl font-heading font-bold text-foreground-900 mt-8 mb-4">
            {line.replace('## ', '')}
          </h2>
        );
      } else if (line.startsWith('### ')) {
        elements.push(
          <h3 key={i} className="text-lg font-heading font-semibold text-foreground-900 mt-6 mb-3">
            {line.replace('### ', '')}
          </h3>
        );
      } else if (line.startsWith('- ')) {
        const listItems: string[] = [];
        while (i < lines.length && lines[i].startsWith('- ')) {
          listItems.push(lines[i].replace('- ', ''));
          i++;
        }
        i--;
        elements.push(
          <ul key={i} className="list-disc list-inside space-y-1 text-foreground-700 mb-4">
            {listItems.map((item, idx) => (
              <li key={idx} className="text-sm leading-relaxed">{item}</li>
            ))}
          </ul>
        );
      } else if (line.startsWith('| ')) {
        const tableLines: string[] = [];
        while (i < lines.length && lines[i].startsWith('| ')) {
          tableLines.push(lines[i]);
          i++;
        }
        i--;
        const headers = tableLines[0].split('|').filter(Boolean).map((h) => h.trim());
        const rows = tableLines.slice(2).map((row) =>
          row.split('|').filter(Boolean).map((cell) => cell.trim())
        );
        elements.push(
          <div key={i} className="overflow-x-auto mb-4">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-background-100">
                  {headers.map((header, idx) => (
                    <th key={idx} className="border border-background-200 px-4 py-2 text-left text-foreground-900 font-medium">
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, rIdx) => (
                  <tr key={rIdx} className={rIdx % 2 === 0 ? 'bg-background-50' : 'bg-background-100'}>
                    {row.map((cell, cIdx) => (
                      <td key={cIdx} className="border border-background-200 px-4 py-2 text-foreground-700">
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      } else if (line.startsWith('Q:')) {
        elements.push(
          <div key={i} className="bg-background-100 rounded-lg p-4 mb-4">
            <p className="text-sm font-medium text-foreground-900 mb-2">{line}</p>
            {i + 1 < lines.length && lines[i + 1].startsWith('A:') && (
              <p className="text-sm text-foreground-600">{lines[i + 1]}</p>
            )}
            {i + 1 < lines.length && lines[i + 1].startsWith('A:') && i++}
          </div>
        );
      } else if (line.trim() !== '') {
        elements.push(
          <p key={i} className="text-sm text-foreground-700 leading-relaxed mb-4">
            {line}
          </p>
        );
      }
      i++;
    }

    return elements;
  };

  return (
    <main className="min-h-screen bg-background-50">
      {/* SEO Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify([
            {
              '@context': 'https://schema.org',
              '@type': 'Article',
              headline: article.title,
              description: article.excerpt,
              author: {
                '@type': 'Person',
                name: article.author,
              },
              datePublished: article.publishedAt,
              image: article.coverImage,
              publisher: {
                '@type': 'Organization',
                name: '榴莲视频',
              },
            },
            {
              '@context': 'https://schema.org',
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: '首页', item: 'https://durian-video.example.com/' },
                ...(category ? [{ '@type': 'ListItem', position: 2, name: category.name, item: `https://durian-video.example.com/category/${category.slug}` }] : []),
                { '@type': 'ListItem', position: category ? 3 : 2, name: article.title },
              ],
            },
          ]),
        }}
      />

      <article className="w-full px-4 md:px-6 pt-24 pb-14">
        <div className="max-w-4xl mx-auto">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              ...(category ? [{ label: category.name, to: `/category/${category.slug}` }] : []),
              { label: article.title },
            ]}
          />

          {/* Article Header */}
          <header className="mb-8">
            <div className="flex flex-wrap items-center gap-2 mb-4">
              {article.tags.map((tag) => (
                <span key={tag} className="inline-flex px-2.5 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-700">
                  {tag}
                </span>
              ))}
            </div>
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-4 leading-tight">
              {article.title}
            </h1>
            <p className="text-base text-foreground-500 mb-6 leading-relaxed">{article.excerpt}</p>
            <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-400">
              <span className="flex items-center gap-1">
                <i className="ri-user-line"></i> {article.author}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line"></i> {article.publishedAt}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-time-line"></i> 阅读约{article.readTime}分钟
              </span>
            </div>
          </header>

          {/* Cover Image */}
          <div className="relative aspect-[16/9] rounded-lg overflow-hidden mb-10">
            <img
              src={article.coverImage}
              alt={article.title}
              title={article.title}
              className="w-full h-full object-cover"
            />
          </div>

          {/* Article Content */}
          <div className="prose-custom">
            {parseContent(article.content)}
          </div>

          {/* Related Platforms */}
          {article.relatedPlatforms.length > 0 && (
            <div className="mt-12 p-6 bg-background-100 rounded-lg">
              <h4 className="text-sm font-semibold text-foreground-900 mb-3">相关平台</h4>
              <div className="flex flex-wrap gap-2">
                {article.relatedPlatforms.map((pId) => {
                  const platform = platforms.find((p) => p.id === pId);
                  const platformArticle = articles.find((a) => a.platform === pId && a.id !== article.id);
                  if (!platform) return null;
                  return platformArticle ? (
                    <Link
                      key={pId}
                      to={`/article/${platformArticle.id}`}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-background-50 rounded-lg text-sm text-foreground-700 hover:text-primary-600 hover:bg-primary-50 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className={`${platform.icon} text-xs`}></i>
                      {platform.name}
                    </Link>
                  ) : (
                    <span
                      key={pId}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-background-50 rounded-lg text-sm text-foreground-500 whitespace-nowrap"
                    >
                      <i className={`${platform.icon} text-xs`}></i>
                      {platform.name}
                    </span>
                  );
                })}
              </div>
            </div>
          )}

          {/* Related Articles */}
          {relatedArticles.length > 0 && (
            <div className="mt-12">
              <h3 className="text-lg font-heading font-bold text-foreground-900 mb-5">相关文章推荐</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                {relatedArticles.map((a) => (
                  <ArticleCard key={a.id} article={a} />
                ))}
              </div>
            </div>
          )}
        </div>
      </article>
    </main>
  );
}